/**
 * CostsSubTab Component
 *
 * Costs sub-tab for managing investment costs with:
 * - Cost types (software, contractor, employee, service, audit, infrastructure)
 * - Frequency-based recurring amounts
 * - Quantitative vs qualitative costs
 * - Monthly/annual calculated totals
 */

import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useCosts, useCreateCost, useUpdateCost, useDeleteCost } from '@medsuite/api-client';
import { CostTypeLabel, FrequencyLabel, QualifiableCostTypeLabel } from '@medsuite/models';
import {
  Input,
  Select,
  TextArea,
  Checkbox,
  Button,
  Card,
  Modal,
  ModalFooter,
  useDialog,
} from '@medsuite/ui-components';
import React, { useState, useEffect, useRef, useMemo } from 'react';

import { CostTypeHelpModal } from './CostTypeHelpModal';

import type { UpdateCostDto, CreateCostDto } from '@medsuite/api-client';
import type {
  Investment,
  InvestmentCost,
  CostType,
  Frequency,
  AmountType,
  QualifiableCostType,
} from '@medsuite/models';

interface CostsSubTabProps {
  investment: Investment;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
}

interface CostFormData {
  cost_type: CostType;
  cost_name: string;
  cost_details: string;
  cost_assumption_details: string;
  is_quantifiable: boolean;
  frequency: Frequency;
  start_date: string | null;
  end_date: string | null;
  amount: number | null;
  amount_type: AmountType | null;
  hourly_rate: number | null;
  hours_per_week: number | null;
  annual_salary: number | null;
  qualifiable_type: QualifiableCostType | null;
  qualifiable_notes: string;
}

/**
 * Format currency helper
 */
const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) {
    return '$0';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Calculate the number of periods between two dates based on frequency
 * Returns appropriate default if no dates provided
 */
const calculatePeriodsBetweenDates = (
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  frequency: string
): { count: number; label: string } => {
  // Defaults when no end date (perpetual)
  const defaults: Record<string, { count: number; label: string }> = {
    weekly: { count: 52, label: 'weeks' },
    monthly: { count: 12, label: 'months' },
    quarterly: { count: 4, label: 'quarters' },
    annually: { count: 1, label: 'year' },
  };

  if (!startDate || !endDate) {
    return defaults[frequency] || defaults.monthly;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive

  switch (frequency) {
    case 'weekly': {
      const weeks = Math.max(1, Math.ceil(diffDays / 7));
      return { count: weeks, label: weeks === 1 ? 'week' : 'weeks' };
    }
    case 'monthly': {
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      const count = Math.max(1, months);
      return { count, label: count === 1 ? 'month' : 'months' };
    }
    case 'quarterly': {
      const months =
        (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      const quarters = Math.max(1, Math.ceil(months / 3));
      return { count: quarters, label: quarters === 1 ? 'quarter' : 'quarters' };
    }
    case 'annually': {
      const years = Math.max(1, end.getFullYear() - start.getFullYear() + 1);
      return { count: years, label: years === 1 ? 'year' : 'years' };
    }
    default:
      return defaults.monthly;
  }
};

/**
 * Calculate the number of months between two dates (for backward compatibility)
 */
const _calculateMonthsBetweenDates = (
  startDate: string | null | undefined,
  endDate: string | null | undefined
): number => {
  return calculatePeriodsBetweenDates(startDate, endDate, 'monthly').count;
};

/**
 * Calculate monthly amount for a cost
 */
const calculateMonthlyAmount = (cost: Record<string, unknown>): number => {
  if (!cost.is_quantifiable) {
    return 0;
  }

  // Handle employee/contractor with salary or hourly
  if (cost.cost_type === 'employee' || cost.cost_type === 'contractor') {
    if (cost.annual_salary) {
      return Number(cost.annual_salary) / 12;
    }
    if (cost.hourly_rate && cost.hours_per_week) {
      return Number(cost.hourly_rate) * Number(cost.hours_per_week) * 4.33;
    }
  }

  // Handle standard amount with frequency
  const amount = Number(cost.amount) || 0;
  switch (cost.frequency) {
    case 'one_time':
      return 0;
    case 'weekly':
      return amount * 4.33;
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'annually':
      return amount / 12;
    default:
      // Legacy fallback
      return (Number(cost.capex_cost) || 0) + (Number(cost.opex_cost) || 0);
  }
};

/**
 * Calculate total cost for the period based on start/end dates and frequency
 * - One-time: return the one-time amount
 * - With dates: calculate periods based on frequency (weeks, months, quarters, years)
 * - Perpetual (no end date): use default annual periods for frequency
 */
const calculateTotalForPeriod = (cost: Record<string, unknown>): number => {
  if (!cost.is_quantifiable) {
    return 0;
  }

  // For one-time costs, return the amount directly
  if (cost.frequency === 'one_time') {
    return Number(cost.amount) || 0;
  }

  const amount = Number(cost.amount) || 0;
  const frequency = (cost.frequency || 'monthly') as string;

  // For employee/contractor, use the calculated monthly amount
  if (cost.cost_type === 'employee' || cost.cost_type === 'contractor') {
    const monthlyAmount = calculateMonthlyAmount(cost);
    const { count } = calculatePeriodsBetweenDates(cost.start_date as string | null | undefined, cost.end_date as string | null | undefined, 'monthly');
    return monthlyAmount * count;
  }

  // For other cost types, calculate based on frequency and period
  const { count } = calculatePeriodsBetweenDates(cost.start_date as string | null | undefined, cost.end_date as string | null | undefined, frequency);
  return amount * count;
};

// Cost type options
const costTypeOptions = Object.entries(CostTypeLabel).map(([value, label]) => ({
  value,
  label,
}));

// Frequency options
const frequencyOptions = Object.entries(FrequencyLabel).map(([value, label]) => ({
  value,
  label,
}));

// Qualifiable type options
const qualifiableTypeOptions = Object.entries(QualifiableCostTypeLabel).map(([value, label]) => ({
  value,
  label,
}));

/**
 * CostsSubTab Component
 */
export const CostsSubTab: React.FC<CostsSubTabProps> = ({
  investment,
  isEditing: _isEditing,
  setIsEditing,
}) => {
  const { confirm } = useDialog();
  const investmentId = investment?.investment_id ?? 0;
  const isValidId = investmentId > 0 && !isNaN(investmentId);

  // Fetch data
  const { data: costs = [], isLoading } = useCosts(investmentId, {
    enabled: isValidId,
  });

  // Mutations
  const createCost = useCreateCost(investmentId);
  const updateCost = useUpdateCost(investmentId);
  const deleteCost = useDeleteCost(investmentId);

  // Local state for display
  const [localCosts, setLocalCosts] = useState<InvestmentCost[]>([]);

  // Track previous values to prevent infinite loops
  const prevInvestmentIdRef = useRef<number | null>(null);
  const prevCostsRef = useRef<string>('');

  // Modal state
  const [costModalOpen, setCostModalOpen] = useState(false);
  const [editingCostId, setEditingCostId] = useState<number | null>(null);
  const [costForm, setCostForm] = useState<CostFormData>({
    cost_type: 'software',
    cost_name: '',
    cost_details: '',
    cost_assumption_details: '',
    is_quantifiable: true,
    frequency: 'monthly',
    start_date: null,
    end_date: null,
    amount: null,
    amount_type: null,
    hourly_rate: null,
    hours_per_week: null,
    annual_salary: null,
    qualifiable_type: null,
    qualifiable_notes: '',
  });
  const [isSavingCost, setIsSavingCost] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);

  // Initialize local state when data loads
  useEffect(() => {
    const costsJson = JSON.stringify(costs);
    if (prevInvestmentIdRef.current !== investmentId || prevCostsRef.current !== costsJson) {
      setLocalCosts(costs);
      prevInvestmentIdRef.current = investmentId;
      prevCostsRef.current = costsJson;
    }
  }, [investmentId, costs]);

  // Calculate totals
  const quantifiableCosts = localCosts.filter((c) => c.is_quantifiable !== false);
  const qualifiableCosts = localCosts.filter((c) => c.is_quantifiable === false);
  const monthlyTotal = quantifiableCosts.reduce((sum, c) => sum + calculateMonthlyAmount(c as unknown as Record<string, unknown>), 0);
  // Use period-based calculation for total (respects start/end dates for employee/contractor hourly costs)
  const periodTotal = quantifiableCosts.reduce((sum, c) => sum + calculateTotalForPeriod(c as unknown as Record<string, unknown>), 0);

  // Handlers
  const handleOpenCostModal = (costId?: number) => {
    if (costId !== undefined) {
      const cost = localCosts.find((c) => c.investment_cost_id === costId);
      if (cost) {
        setEditingCostId(costId);
        setCostForm({
          cost_type: cost.cost_type || 'software',
          cost_name: cost.cost_name || '',
          cost_details: cost.cost_details || '',
          cost_assumption_details: cost.cost_assumption_details || '',
          is_quantifiable: cost.is_quantifiable !== false,
          frequency: cost.frequency || 'monthly',
          start_date: cost.start_date
            ? new Date(cost.start_date).toISOString().split('T')[0]
            : null,
          end_date: cost.end_date ? new Date(cost.end_date).toISOString().split('T')[0] : null,
          amount: cost.amount,
          amount_type: cost.amount_type,
          hourly_rate: cost.hourly_rate,
          hours_per_week: cost.hours_per_week,
          annual_salary: cost.annual_salary,
          qualifiable_type: cost.qualifiable_type,
          qualifiable_notes: cost.qualifiable_notes || '',
        });
      }
    } else {
      setEditingCostId(null);
      setCostForm({
        cost_type: 'software',
        cost_name: '',
        cost_details: '',
        cost_assumption_details: '',
        is_quantifiable: true,
        frequency: 'monthly',
        start_date: null,
        end_date: null,
        amount: null,
        amount_type: null,
        hourly_rate: null,
        hours_per_week: null,
        annual_salary: null,
        qualifiable_type: null,
        qualifiable_notes: '',
      });
    }
    setCostModalOpen(true);
  };

  const handleCloseCostModal = () => {
    setCostModalOpen(false);
    setEditingCostId(null);
    setIsSavingCost(false);
  };

  const handleSaveCost = async () => {
    if (!isValidId) {
      return;
    }
    setIsSavingCost(true);

    try {
      const payload = {
        cost_type: costForm.cost_type,
        cost_name: costForm.cost_name,
        cost_details: costForm.cost_details || undefined,
        cost_assumption_details: costForm.cost_assumption_details || undefined,
        is_quantifiable: costForm.is_quantifiable,
        frequency: costForm.frequency,
        start_date: costForm.start_date || undefined,
        end_date: costForm.end_date || undefined,
        amount: costForm.amount,
        amount_type: costForm.amount_type,
        hourly_rate: costForm.hourly_rate,
        hours_per_week: costForm.hours_per_week,
        annual_salary: costForm.annual_salary,
        qualifiable_type: costForm.qualifiable_type,
        qualifiable_notes: costForm.qualifiable_notes || undefined,
      };

      if (editingCostId !== null) {
        await updateCost.mutateAsync({
          costId: editingCostId,
          data: payload as unknown as UpdateCostDto,
        });
      } else {
        await createCost.mutateAsync(payload as unknown as Omit<CreateCostDto, 'investment_id'>);
      }
      setIsEditing(true);
      handleCloseCostModal();
    } catch (error) {
      console.error('Failed to save cost:', error);
      setIsSavingCost(false);
    }
  };

  const handleDeleteCost = async (costId: number) => {
    const confirmed = await confirm({
      title: 'Delete Cost',
      message: 'Are you sure you want to delete this cost? This action cannot be undone.',
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteCost.mutateAsync(costId);
      setIsEditing(true);
    } catch (error) {
      console.error('Failed to delete cost:', error);
    }
  };

  // Show employee/contractor fields
  const showEmployeeFields =
    costForm.cost_type === 'employee' || costForm.cost_type === 'contractor';

  // Calculate monthly for form preview
  const formMonthly = useMemo(() => {
    if (!costForm.is_quantifiable) {
      return 0;
    }
    if (showEmployeeFields) {
      if (costForm.annual_salary) {
        return costForm.annual_salary / 12;
      }
      if (costForm.hourly_rate && costForm.hours_per_week) {
        return costForm.hourly_rate * costForm.hours_per_week * 4.33;
      }
    }
    return 0;
  }, [costForm, showEmployeeFields]);

  // Render cost modal
  const renderCostModal = () => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      handleSaveCost();
    };

    return (
      <Modal
        isOpen={costModalOpen}
        onClose={handleCloseCostModal}
        title={editingCostId !== null ? 'Edit Cost' : 'Add Cost'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
            {/* Cost Type */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cost Type *
                </label>
                <button
                  type="button"
                  onClick={() => setHelpModalOpen(true)}
                  disabled={!costForm.cost_type}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded-full"
                  title={costForm.cost_type ? 'Help for this cost type' : 'Select a cost type first'}
                >
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>
              </div>
              <Select
                options={costTypeOptions}
                value={costForm.cost_type}
                onChange={(value) => setCostForm({ ...costForm, cost_type: value as CostType })}
                required
              />
            </div>

            {/* Cost Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cost Name *
              </label>
              <Input
                value={costForm.cost_name}
                onChange={(e) => setCostForm({ ...costForm, cost_name: e.target.value })}
                placeholder="Enter cost name"
                required
              />
            </div>

            {/* Cost Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Cost Details
              </label>
              <TextArea
                value={costForm.cost_details}
                onChange={(e) => setCostForm({ ...costForm, cost_details: e.target.value })}
                placeholder="Enter cost details"
                rows={2}
              />
            </div>

            {/* Is Quantitative */}
            <div>
              <label className="flex items-center space-x-2">
                <Checkbox
                  checked={costForm.is_quantifiable}
                  onChange={(e) => setCostForm({ ...costForm, is_quantifiable: e.target.checked })}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Quantitative Cost
                </span>
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Uncheck for qualitative costs (risks without dollar values)
              </p>
            </div>

            {/* Quantitative fields */}
            {costForm.is_quantifiable && (
              <>
                {/* Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Frequency *
                  </label>
                  <Select
                    options={frequencyOptions}
                    value={costForm.frequency}
                    onChange={(value) =>
                      setCostForm({ ...costForm, frequency: value as Frequency })
                    }
                    required
                  />
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start Date
                    </label>
                    <Input
                      type="date"
                      value={costForm.start_date || ''}
                      onChange={(e) =>
                        setCostForm({ ...costForm, start_date: e.target.value || null })
                      }
                    />
                  </div>
                  {costForm.frequency !== 'one_time' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        End Date
                      </label>
                      <Input
                        type="date"
                        value={costForm.end_date || ''}
                        onChange={(e) =>
                          setCostForm({ ...costForm, end_date: e.target.value || null })
                        }
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for perpetual</p>
                    </div>
                  )}
                </div>

                {/* Employee/Contractor fields */}
                {showEmployeeFields ? (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {costForm.cost_type === 'employee' ? 'Employee' : 'Contractor'} Compensation
                    </h4>
                    <p className="text-xs text-gray-500 mb-4">
                      Enter either annual salary OR hourly rate with hours per week
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Annual Salary ($)
                      </label>
                      <Input
                        type="number"
                        value={costForm.annual_salary || ''}
                        onChange={(e) =>
                          setCostForm({
                            ...costForm,
                            annual_salary: parseFloat(e.target.value) || null,
                          })
                        }
                        placeholder="e.g., 75000"
                        min="0"
                      />
                    </div>

                    <div className="text-center text-gray-500 my-2">- or -</div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hourly Rate ($)
                        </label>
                        <Input
                          type="number"
                          value={costForm.hourly_rate || ''}
                          onChange={(e) =>
                            setCostForm({
                              ...costForm,
                              hourly_rate: parseFloat(e.target.value) || null,
                            })
                          }
                          placeholder="e.g., 50"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Hours Per Week
                        </label>
                        <Input
                          type="number"
                          value={costForm.hours_per_week || ''}
                          onChange={(e) =>
                            setCostForm({
                              ...costForm,
                              hours_per_week: parseFloat(e.target.value) || null,
                            })
                          }
                          placeholder="e.g., 40"
                          min="0"
                          max="168"
                        />
                      </div>
                    </div>

                    {formMonthly > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          <strong>Calculated Monthly Cost:</strong> {formatCurrency(formMonthly)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Standard amount fields */
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount ($)
                      </label>
                      <Input
                        type="number"
                        value={costForm.amount || ''}
                        onChange={(e) =>
                          setCostForm({ ...costForm, amount: parseFloat(e.target.value) || null })
                        }
                        placeholder="0"
                        min="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">Amount per {costForm.frequency}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Amount Type
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Select type' },
                          { value: 'C', label: 'CapEx' },
                          { value: 'O', label: 'OpEx' },
                        ]}
                        value={costForm.amount_type || ''}
                        onChange={(value) =>
                          setCostForm({
                            ...costForm,
                            amount_type: (value || null) as AmountType | null,
                          })
                        }
                      />
                    </div>
                  </div>
                )}

                {/* Assumptions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Assumptions
                  </label>
                  <TextArea
                    value={costForm.cost_assumption_details}
                    onChange={(e) =>
                      setCostForm({ ...costForm, cost_assumption_details: e.target.value })
                    }
                    placeholder="Document cost assumptions"
                    rows={2}
                  />
                </div>
              </>
            )}

            {/* Qualitative (non-quantitative) fields */}
            {!costForm.is_quantifiable && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Risk Classification
                </h4>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Risk Type
                  </label>
                  <Select
                    options={[{ value: '', label: 'Select risk type' }, ...qualifiableTypeOptions]}
                    value={costForm.qualifiable_type || ''}
                    onChange={(value) =>
                      setCostForm({
                        ...costForm,
                        qualifiable_type: (value || null) as QualifiableCostType | null,
                      })
                    }
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Risk Notes
                  </label>
                  <TextArea
                    value={costForm.qualifiable_notes}
                    onChange={(e) =>
                      setCostForm({ ...costForm, qualifiable_notes: e.target.value })
                    }
                    placeholder="Describe the nature and potential impact of this risk"
                    rows={3}
                  />
                </div>
              </div>
            )}
          </div>

          <ModalFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={handleCloseCostModal}
              disabled={isSavingCost}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSavingCost}>
              {isSavingCost ? 'Saving...' : editingCostId !== null ? 'Save Changes' : 'Add Cost'}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    );
  };

  // Guard: Show loading state
  if (!isValidId) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Loading investment data...
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading costs...</div>;
  }

  return (
    <>
      {renderCostModal()}
      <CostTypeHelpModal
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        costType={costForm.cost_type}
      />
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Investment Costs
            </h3>
            <Button onClick={() => handleOpenCostModal()} size="sm">
              + Add Cost
            </Button>
          </div>

          {localCosts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-gray-500 dark:text-gray-400 mb-4">No costs added yet.</p>
              <Button onClick={() => handleOpenCostModal()} variant="outline">
                Add Your First Cost
              </Button>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Frequency
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        CapEx/OpEx
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Monthly
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Annual
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {localCosts.map((cost) => {
                      const monthly = calculateMonthlyAmount(cost as unknown as Record<string, unknown>);
                      const isOneTime = cost.frequency === 'one_time';
                      const costTypeLabel = cost.cost_type
                        ? CostTypeLabel[cost.cost_type as CostType]
                        : '-';
                      const frequencyLabel = cost.frequency
                        ? FrequencyLabel[cost.frequency as Frequency]
                        : cost.is_perpetual
                          ? 'Perpetual'
                          : 'One-time';

                      return (
                        <tr
                          key={cost.investment_cost_id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="px-4 py-4 text-sm">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {costTypeLabel}
                            </span>
                            {!cost.is_quantifiable && (
                              <span className="block text-xs text-orange-600">Qualitative</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {cost.cost_name || '-'}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {frequencyLabel}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100">
                            {cost.amount_type === 'C'
                              ? 'CapEx'
                              : cost.amount_type === 'O'
                                ? 'OpEx'
                                : '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900 dark:text-gray-100 text-right font-mono">
                            {cost.is_quantifiable !== false ? (
                              isOneTime ? (
                                <span className="text-gray-500">
                                  {formatCurrency(cost.amount)}
                                  <span className="block text-xs">one-time</span>
                                </span>
                              ) : (
                                formatCurrency(monthly)
                              )
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100 text-right font-mono">
                            {cost.is_quantifiable !== false
                              ? isOneTime
                                ? formatCurrency(cost.amount)
                                : formatCurrency(monthly * 12)
                              : '-'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm space-x-2">
                            <Button
                              onClick={() => handleOpenCostModal(cost.investment_cost_id)}
                              variant="ghost"
                              size="sm"
                              className="text-indigo-600 dark:text-indigo-400"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeleteCost(cost.investment_cost_id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-600 dark:text-red-400"
                            >
                              Delete
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      Costs
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                      {quantifiableCosts.length} quantitative
                    </div>
                    {qualifiableCosts.length > 0 && (
                      <div className="text-sm text-orange-600">
                        {qualifiableCosts.length} qualitative
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      Monthly Total
                    </div>
                    <div className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
                      {formatCurrency(monthlyTotal)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      Period Total
                    </div>
                    <div className="mt-1 text-xl font-bold text-red-600">
                      {formatCurrency(periodTotal)}
                    </div>
                  </div>
                  <div className="border-l border-gray-200 dark:border-gray-700 pl-4">
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                      By Type
                    </div>
                    <div className="mt-1 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-blue-600">CapEx:</span>
                        <span className="font-mono">
                          {formatCurrency(
                            quantifiableCosts
                              .filter((c) => c.amount_type === 'C')
                              .reduce((sum, c) => sum + calculateTotalForPeriod(c as unknown as Record<string, unknown>), 0)
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">OpEx:</span>
                        <span className="font-mono">
                          {formatCurrency(
                            quantifiableCosts
                              .filter((c) => c.amount_type === 'O')
                              .reduce((sum, c) => sum + calculateTotalForPeriod(c as unknown as Record<string, unknown>), 0)
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </>
  );
};

export default CostsSubTab;
